import { Trash } from '@phosphor-icons/react';
import type { PersonaDto } from '@reelplicate/api-client/model';
import {
  getGetPersonaQueryKey,
  getListPersonasQueryKey,
} from '@reelplicate/api-client/personas';
import {
  getListSocialAccountsQueryKey,
  useCreateSocialAccount,
  useDeleteSocialAccount,
} from '@reelplicate/api-client/social-accounts';
import {
  BrandMark,
  Button,
  IconButton,
  Inline,
  Stack,
  TextInput,
  toast,
} from '@reelplicate/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useId, useState } from 'react';

import { PlatformSelect } from '@/features/accounts/platform-select';
import {
  linkPlatform,
  PERSONA_PLATFORMS,
  type PersonaPlatform,
} from '@/features/accounts/platforms';
import { PLATFORM_LABELS } from '@/features/settings/platforms';
import { getErrorMessage } from '@/lib/errors';

const MARKS = {
  TIKTOK: 'tiktok',
  INSTAGRAM: 'instagram',
  YOUTUBE: 'youtube',
  LINKEDIN: 'linkedin',
  FACEBOOK: 'facebook',
} as const;

/**
 * A persona's accounts (Figma 434:3338): one row each - mark, handle and
 * platform - then a row to add another by handle or profile link. Pasting a
 * link picks its platform. Accounts are known by handle until publishing
 * integrations can connect them, so rows carry no connection state yet.
 */
export function PersonaAccounts({
  workspaceId,
  persona,
  canEdit,
}: {
  workspaceId: string;
  persona: PersonaDto;
  canEdit: boolean;
}) {
  const inputId = useId();
  const queryClient = useQueryClient();
  const create = useCreateSocialAccount({ mutation: { meta: { silent: true } } });
  const remove = useDeleteSocialAccount();
  const [platform, setPlatform] = useState<PersonaPlatform>('TIKTOK');
  const [handle, setHandle] = useState('');
  const [error, setError] = useState<string | null>(null);

  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: getListPersonasQueryKey(workspaceId) }),
      queryClient.invalidateQueries({
        queryKey: getGetPersonaQueryKey(workspaceId, persona.id),
      }),
      queryClient.invalidateQueries({
        queryKey: getListSocialAccountsQueryKey(workspaceId),
      }),
    ]);

  const add = async () => {
    if (!handle.trim()) {
      setError('Enter a handle or paste a profile link');
      return;
    }
    setError(null);
    try {
      await create.mutateAsync({
        id: workspaceId,
        data: { platform, handle: handle.trim(), personaId: persona.id },
      });
    } catch (e) {
      setError(getErrorMessage(e));
      return;
    }
    setHandle('');
    await refresh();
  };

  const removeAccount = async (account: PersonaDto['accounts'][number]) => {
    try {
      await remove.mutateAsync({ id: workspaceId, accountId: account.id });
    } catch {
      return; // Shown as a toast.
    }
    await refresh();
    toast.success(`${account.handle} removed`);
  };

  return (
    <Stack gap={0}>
      {persona.accounts.length > 0 && (
        <ul
          aria-label={`${persona.name}’s accounts`}
          className="divide-border-subtle divide-y"
        >
          {persona.accounts.map((account) => (
            <li key={account.id} className="flex items-center gap-3.5 py-3.5">
              <span className="bg-bg-subtle flex size-10 shrink-0 items-center justify-center rounded-md">
                <BrandMark name={MARKS[account.platform]} size={20} />
              </span>
              <Stack gap={0.5} className="min-w-0 flex-1">
                <p className="text-body-md-strong text-text-primary truncate">
                  {account.handle}
                </p>
                <p className="text-caption text-text-secondary">
                  {PLATFORM_LABELS[account.platform]}
                </p>
              </Stack>
              {canEdit && (
                <IconButton
                  type="button"
                  icon={Trash}
                  size="sm"
                  variant="ghost"
                  aria-label={`Remove ${account.handle}`}
                  onClick={() => void removeAccount(account)}
                />
              )}
            </li>
          ))}
        </ul>
      )}
      {persona.accounts.length === 0 && (
        <p className="text-body-sm text-text-tertiary py-3.5">
          {canEdit
            ? `No accounts yet - add the handles ${persona.shortName ?? persona.name} posts from.`
            : 'No accounts yet.'}
        </p>
      )}
      {canEdit && (
        <Stack gap={1.5} className="pt-3.5">
          <Inline gap={2} alignY="center">
            <PlatformSelect
              size="sm"
              value={platform}
              onValueChange={setPlatform}
              platforms={PERSONA_PLATFORMS}
              label={`New account platform for ${persona.name}`}
            />
            <TextInput
              id={inputId}
              size="sm"
              className="min-w-0 flex-1"
              autoComplete="off"
              placeholder="@handle or profile link"
              aria-label={`New account for ${persona.name}`}
              aria-invalid={Boolean(error)}
              value={handle}
              onChange={(event) => {
                const next = event.target.value;
                setHandle(next);
                setError(null);
                const detected = linkPlatform(next, PERSONA_PLATFORMS);
                if (detected) setPlatform(detected);
              }}
              onKeyDown={(event) => {
                // The page is a form (profile); Enter here adds the account instead.
                if (event.key === 'Enter') {
                  event.preventDefault();
                  void add();
                }
              }}
            />
            <Button
              type="button"
              size="sm"
              variant="outline"
              loading={create.isPending}
              onClick={() => void add()}
            >
              Add account
            </Button>
          </Inline>
          {error ? (
            <p role="alert" className="text-caption text-status-danger-fg">
              {error}
            </p>
          ) : (
            <p className="text-caption text-text-tertiary">
              Paste a profile link and we pick the platform for you.
            </p>
          )}
        </Stack>
      )}
    </Stack>
  );
}
